// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import _ from "es-toolkit/compat";
import i18n from "i18next";
import { atom } from "jotai";

import { logApiError } from "../../infra/apiError";
import { showDedupedErrorToast } from "../../infra/dedupedToast";
import { fetchJson } from "../../infra/fetchJson";
import type { ResourceSlot } from "../backendai";
import { availableProjectListAtom } from "./projects";

export const resourceSlotListAtom = atom(async (get) => {
  try {
    const { cpu, mem, ...resourceSlots } = await fetchJson<
      Record<string, string>
    >("/func/config/resource-slots");

    const slots = _.keys(resourceSlots).map((slot) => slot as ResourceSlot);
    // Add cuda.mem as a virtual slot when cuda.shares is supported
    return slots.includes("cuda.shares")
      ? ([...slots, "cuda.mem"] as ResourceSlot[])
      : slots;
  } catch (e) {
    // Non-2xx errors (e.g. 429 on rate-limited backends, 5xx during
    // outages) must not tear down consumers via Suspense/ErrorBoundary.
    // PipelineYamlEditor reads this atom unconditionally on mount, so a
    // throw here nukes the entire PipelineDetail subtree into the
    // SectionErrorFallback at PipelineList. Symmetric with folderAtom:
    // log, surface one toast, and degrade to an empty slot list. See
    // issue #3382.
    logApiError("resourceSlotListAtom:/func/config/resource-slots", e);
    showDedupedErrorToast(
      "resourceSlot:load",
      i18n.t("error.FailedToLoadResourceSlots")
    );
    return [] as ResourceSlot[];
  }
});

interface ProjectResourceGroup {
  scaling_groups: {
    name: string;
  }[];
}

export const projectResourceGroupListAtom = atom(async (get) => {
  try {
    const projects = await get(availableProjectListAtom);
    const resourceGroups: ProjectResourceGroup[] = await Promise.all(
      _.map(projects, async (project) => {
        return fetchJson<ProjectResourceGroup>(
          `/func/scaling-groups?group=${project.name}`
        );
      })
    );
    return {
      projects: _.map(projects, (project, idx: number) => {
        return {
          name: project.name,
          resourceGroup: _.map(
            resourceGroups[idx].scaling_groups?.filter(
              (rg) => rg.name !== "upload"
            ),
            (rg) => rg.name
          ),
        };
      }),
    };
  } catch (e) {
    logApiError("projectResourceGroupListAtom:/func/scaling-groups", e);
    showDedupedErrorToast(
      "resourceGroup:load",
      i18n.t("error.FailedToLoadResourceGroups")
    );
    return { projects: [] };
  }
});
